import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { BroadcastService } from 'src/app/services/broadcast.service';
import { Action, ActionType } from 'src/app/models/action';
import { UserService } from 'src/app/services/user.service';
import { LocaleService } from '../../../services/locale.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Util } from '../../../util';
import { ConfigService } from '../../../services/config.service';
import { ParamsLoginOauth } from './models/params-login-oauth';
import { ApiService } from '../../../services/api.service';
import { PlayerUrlAnnotationService } from 'src/app/services/api/player-url-annotation.service';
import { ToastService } from '../../../_core/services/toast.service';

@Component({
  selector: 'app-login-callback',
  templateUrl: 'login-callback.component.html',
  styleUrls: ['login-callback.component.scss'],
})
export class LoginCallbackComponent implements OnInit {
  private readonly toastService = inject(ToastService);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private localeService: LocaleService,
    private authService: AuthService,
    private userService: UserService,
    private configService: ConfigService,
    private broadcastService: BroadcastService,
    private apiService: ApiService,
    private playerUrlAnnotationService: PlayerUrlAnnotationService
  ) {}

  ngOnInit() {
    let paramsLoginOauth: ParamsLoginOauth = {
      state: null,
      sessionState: null,
      idToken: null,
      accessToken: null,
      tokenType: null,
      expires: null,
    };
    this.route.fragment.subscribe((fragment: string) => {
      let params = new URLSearchParams(fragment);
      paramsLoginOauth = {
        state: params.get('state'),
        sessionState: params.get('session_state'),
        idToken: params.get('id_token'),
        accessToken: params.get('access_token'),
        tokenType: params.get('token_type'),
        expires: params.get('expires_in'),
      };
    });
    this.route.queryParams.subscribe((params) => {
      params = paramsLoginOauth.accessToken ? paramsLoginOauth : params;
      if (params.redirectTo && this.configService.get().customLoginUrl) {
        localStorage.setItem('redirectTo', params.redirectTo);
        this.authService
          .getExternalLoginUrl(this.configService.get().customLoginUrl)
          .subscribe((url) => (window.location.href = url));
        return;
      }

      this.authService.externalLogin(params).subscribe(
        (user) => {
          this.toastService.showToast('messages.login.success');
          this.broadcastService.broadcastLogin(true);
          this.authService.loadProfilePicture(user).subscribe();

          const action: Action = Util.getAction();
          let routeParams: string[] = ['/'];

          if (action) {
            if (action.type === ActionType.OPEN_ANNOTATION) {
              this.playerUrlAnnotationService
              .getPlayerUrlAnnotation(action.recordId, action.annotationId)
              .subscribe((url) => window.open(url, '_self'));
              return;
            }
            routeParams = [new URL(action.redirectUrl).pathname];
          }

          const redirectToOle = localStorage.getItem('redirectToOle');
          if (redirectToOle != null && redirectToOle === 'Y') {
            const bookClubParams = {
              patronId: this.userService.getUserId(),
            };

            this.apiService.getLinkToBookclub(bookClubParams).subscribe((url) => (window.location.href = url));
            return;
          }

          const redirectToLearningExperience = localStorage.getItem('redirectToLearningExperience');
          if (redirectToLearningExperience != null && redirectToLearningExperience === 'Y') {
            localStorage.removeItem('redirectToLearningExperience');
            const learningExperienceParams = {
              patronId: this.userService.getUserId(),
              clubId: localStorage.getItem('clubId'),
            };

            this.apiService.getLinkToBookclub(learningExperienceParams).subscribe((url) => {
              if (!url.includes('clubId')) {
                url = url + '&clubId=' + params['clubId'];
              }
              window.open(url, '_blank', 'noopener');
            });
            this.router.navigate([localStorage.getItem('returnFromLearningExperience')]);
            return;
          }

          const redirectTo = localStorage.getItem('redirectTo');
          if (redirectTo) {
            window.location.href = redirectTo;
            return;
          }

          this.router.navigate(routeParams).then(() => (window as any).location.reload());
        },
        (error) => {
          if (error instanceof HttpErrorResponse) {
            const apiError = error.error;

            if (this.shouldHandleKrsError(error)) {
              // message = { "code": "KRS-X", "error": "..." }
              return this.router.navigate(['/']).then(() => this.toastService.showToast(apiError.errror, 'error'));
            } else if (this.shouldHandleKrfError(error)) {
              // message = { "code": "KRS-X", "error": "...", "url": "..." }
              if (apiError.url) {
                window.location.href = apiError.url;
                return;
              }
            }
          }

          throw error;
        }
      );
    });
  }

  private shouldHandleKrsError(error: HttpErrorResponse): boolean {
    const krsErrors = ['KRS-1', 'KRS-2', 'KRS-3', 'KRS-4', 'KRS-5'];
    return this.checkKbError(error, krsErrors);
  }

  private shouldHandleKrfError(error: HttpErrorResponse): boolean {
    const krfErrors = ['KRS-6', 'KRS-7', 'KRS-8'];
    return this.checkKbError(error, krfErrors);
  }

  private checkKbError(error: HttpErrorResponse, errorList: string[]): boolean {
    return error.status === 401 && this.checkErrorInList(error.error, errorList);
  }

  private checkErrorInList(error: any, errorList: string[]): boolean {
    const code = error.code || '';
    return errorList.some((c) => code === c);
  }
}
